import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { trackingApi, type ChatMessage } from "@/features/delivery/api";
import { formatApiError } from "@/features/auth/formatError";
import { cn } from "@/lib/utils";

type Props = {
    orderId: string;
    me: "customer" | "rider";
};

export function ChatPanel({ orderId, me }: Props) {
    const qc = useQueryClient();
    const [text, setText] = useState("");
    const bottomRef = useRef<HTMLDivElement | null>(null);

    const { data, isLoading } = useQuery({
        queryKey: ["chat", orderId],
        queryFn: () => trackingApi.chat(orderId),
        refetchInterval: 5000,
        enabled: Boolean(orderId),
    });

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [data?.messages?.length]);

    const sendMut = useMutation({
        mutationFn: (t: string) => trackingApi.postChat(orderId, t),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["chat", orderId] });
            setText("");
        },
        onError: (err) => toast.error(formatApiError(err, "Message not sent")),
    });

    const onSend = (e: React.FormEvent) => {
        e.preventDefault();
        const t = text.trim();
        if (!t) return;
        sendMut.mutate(t);
    };

    return (
        <div data-testid="chat-panel" className="flex h-80 flex-col rounded-2xl border border-border/70 bg-card shadow-soft">
            <div className="border-b border-border/60 px-4 py-3">
                <h3 className="text-sm font-bold">Chat with your {me === "customer" ? "rider" : "customer"}</h3>
                <p className="text-[11px] text-muted-foreground">
                    Messages refresh every 5 seconds
                </p>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto px-4 py-3">
                {isLoading ? (
                    <p className="text-xs text-muted-foreground">Loading…</p>
                ) : (data?.messages?.length || 0) === 0 ? (
                    <p className="text-xs text-muted-foreground">
                        Say hi — messages are visible to both of you and the admin console.
                    </p>
                ) : (
                    (data?.messages || []).map((m: ChatMessage) => (
                        <div
                            key={m.id}
                            className={cn(
                                "max-w-[80%] rounded-2xl px-3 py-2 text-sm",
                                m.sender_role === me
                                    ? "ml-auto rounded-tr-sm bg-brand text-white"
                                    : "mr-auto rounded-tl-sm bg-muted",
                            )}
                        >
                            {m.text}
                            <p
                                className={cn(
                                    "mt-0.5 text-[10px]",
                                    m.sender_role === me ? "text-white/70" : "text-muted-foreground",
                                )}
                            >
                                {m.created_at ? new Date(m.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : ""}
                            </p>
                        </div>
                    ))
                )}
                <div ref={bottomRef} />
            </div>
            <form onSubmit={onSend} className="flex gap-2 border-t border-border/60 p-3">
                <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Type a quick message…"
                    maxLength={500}
                    data-testid="chat-input"
                    className="flex-1 rounded-full border border-input bg-background px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
                <button
                    type="submit"
                    data-testid="chat-send"
                    disabled={sendMut.isPending || !text.trim()}
                    className="grid h-10 w-10 place-items-center rounded-full bg-brand text-white shadow-brand disabled:opacity-50"
                >
                    <Send className="h-4 w-4" />
                </button>
            </form>
        </div>
    );
}

export default ChatPanel;
